import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { adOutcomesApi } from '../../api/adOutcomesApi';
import { adOutcomeKeys } from '../../queryKeys';
import {
  EMPTY_DRAFT,
  lastFullMonth,
  outcomeRows,
  type OutcomeDraft,
} from '../../utils/adOutcomesForm';
import styles from './AdOutcomesCard.module.css';

interface Props {
  practiceId: string;
  locationId: string;
}

const FIELDS: Array<{ key: keyof OutcomeDraft; label: string }> = [
  { key: 'callersMatched', label: 'Callers matched' },
  { key: 'newPatientHouseholds', label: 'New households' },
  { key: 'newPatients', label: 'New patients' },
];

/**
 * New patients from ads: the counts the practice sends back after matching the callers from its ads
 * against its own patient records. Counts only, per campaign and period; Jenny's weekly report shows
 * them as practice-reported, with cost per new patient. Nothing that identifies a person is entered.
 */
export function AdOutcomesCard({ practiceId, locationId }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryKey = adOutcomeKeys.list(practiceId, locationId);
  const initial = lastFullMonth(new Date());
  const [periodStart, setPeriodStart] = useState(initial.start);
  const [periodEnd, setPeriodEnd] = useState(initial.end);
  const [drafts, setDrafts] = useState<Record<string, OutcomeDraft>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => adOutcomesApi.get(practiceId, locationId),
  });

  const saveMutation = useMutation({
    mutationFn: (
      rows: NonNullable<Extract<ReturnType<typeof outcomeRows>, { ok: true }>['rows']>,
    ) => adOutcomesApi.save(practiceId, locationId, { periodStart, periodEnd, rows }),
    onSuccess: (result) => {
      toast.success(`Saved ${result.saved} ${result.saved === 1 ? 'campaign' : 'campaigns'}.`);
      setDrafts({});
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not save the counts.'),
  });

  if (isLoading) {
    return (
      <Card title="New patients from ads">
        <Spinner size="sm" />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card title="New patients from ads">
        <p className={styles.intro}>Could not load the counts.</p>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </Card>
    );
  }

  const setField = (campaign: string, key: keyof OutcomeDraft, value: string) =>
    setDrafts((d) => ({ ...d, [campaign]: { ...(d[campaign] ?? EMPTY_DRAFT), [key]: value } }));

  const save = () => {
    if (!periodStart || !periodEnd || periodEnd < periodStart) {
      toast.error('Choose a period whose end is on or after its start.');
      return;
    }
    const result = outcomeRows(drafts);
    if (!result.ok) {
      toast.error(`Whole numbers only: check ${result.invalid.join(', ')}.`);
      return;
    }
    if (result.rows.length === 0) {
      toast.error('Enter at least one count.');
      return;
    }
    saveMutation.mutate(result.rows);
  };

  const latestEnd = data.outcomes[0]?.periodEnd;
  const latest = data.outcomes.filter((o) => o.periodEnd === latestEnd);

  return (
    <Card title="New patients from ads">
      <p className={styles.intro}>
        What the practice found when it matched callers from its ads against its patient records, as
        counts per campaign. Leave a box blank if the practice didn&apos;t send it. Never enter
        names or phone numbers.
      </p>
      {data.campaigns.length === 0 ? (
        <p className={styles.intro}>No campaigns are counted for this location yet.</p>
      ) : (
        <>
          <div className={styles.period}>
            <Input
              type="date"
              label="Period start"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <Input
              type="date"
              label="Period end"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campaign</th>
                {FIELDS.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.name}>
                  <td>{c.name}</td>
                  {FIELDS.map((f) => (
                    <td key={f.key}>
                      <input
                        className={styles.count}
                        inputMode="numeric"
                        aria-label={`${f.label}, ${c.name}`}
                        value={drafts[c.name]?.[f.key] ?? ''}
                        onChange={(e) => setField(c.name, f.key, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.actions}>
            <Button variant="primary" size="sm" onClick={save} disabled={saveMutation.isPending}>
              Save counts
            </Button>
          </div>
        </>
      )}
      {latest.length > 0 && (
        <div className={styles.saved}>
          <div className={styles.savedTitle}>
            Last sent: {latest[0].periodStart} to {latest[0].periodEnd}
          </div>
          <ul className={styles.savedList}>
            {latest.map((o) => (
              <li key={o.campaign}>
                {o.campaign}: {o.newPatients ?? 'n/a'} new patients
                {o.callersMatched !== null && `, ${o.callersMatched} callers matched`}
                {o.matchedCampaign === null && ' (not matched to a campaign by name)'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
